import { Dispatch, SetStateAction } from "react";
import { DictationRefs } from "./dictation-refs.utils";

export function detectSpeechFromPCM(
  inputData: Float32Array,
  refs: DictationRefs,
  setIsSpeaking: Dispatch<SetStateAction<boolean>>
): void {
  let sum = 0;
  const sampleStep = 100;
  for (let i = 0; i < inputData.length; i += sampleStep) {
    sum += Math.abs(inputData[i] ?? 0);
  }
  const avg = sum / (inputData.length / sampleStep);

  if (avg > 0.01) {
    if (!refs.isSpeakingRef.current) setIsSpeaking(true);
    if (refs.silenceTimerRef.current) {
      clearTimeout(refs.silenceTimerRef.current);
      refs.silenceTimerRef.current = null;
    }
  } else {
    if (refs.isSpeakingRef.current && !refs.silenceTimerRef.current) {
      refs.silenceTimerRef.current = setTimeout(() => {
        setIsSpeaking(false);
        refs.silenceTimerRef.current = null;
      }, 500);
    }
  }
}

export function resampleAudio(
  inputData: Float32Array,
  sourceSampleRate: number,
  targetSampleRate: number
): Float32Array {
  if (sourceSampleRate === targetSampleRate) return inputData;

  const sampleRateRatio = sourceSampleRate / targetSampleRate;
  const newLength = Math.round(inputData.length / sampleRateRatio);
  const resampledData = new Float32Array(newLength);

  for (let i = 0; i < newLength; i++) {
    const srcIndex = i * sampleRateRatio;
    const srcIndexInt = Math.floor(srcIndex);
    const srcIndexFrac = srcIndex - srcIndexInt;

    if (srcIndexInt + 1 < inputData.length) {
      const val1 = inputData[srcIndexInt] ?? 0;
      const val2 = inputData[srcIndexInt + 1] ?? 0;
      resampledData[i] = val1 * (1 - srcIndexFrac) + val2 * srcIndexFrac;
    } else {
      resampledData[i] = inputData[srcIndexInt] ?? 0;
    }
  }
  return resampledData;
}

export function convertFloat32ToInt16(resampledData: Float32Array): Int16Array {
  const pcmData = new Int16Array(resampledData.length);
  for (let i = 0; i < resampledData.length; i++) {
    const val = resampledData[i] ?? 0;
    const s = Math.max(-1, Math.min(1, val));
    pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return pcmData;
}
